<template>
<div>
  <div class="board" v-if="experiment" id="pdf-board">
    <div class="board__row" v-for="(row, y) in experiment.height" :key="y">
      <div class="board__item"
        v-for="(columns, x) in experiment.width" :key="x"
        @click="selectBlock(well(x, y))"
        @dblclick="showModal(well(x, y))"
        :class="wellClass(x, y)"
        v-on-clickaway="closeModal">
        <span class="board__number text-uppercase" v-if="x === 0 || y === 0">
          {{ wellCoordinates(x, y) }}
        </span>
        <!-- TEST BLOCKS -->
        <p v-if="wells[y][x].test">{{ wells[y][x].test }}</p>
        <!-- MODAL -->
        <div class="well__activity" v-if="modalFor === well(x, y)">
            <h2 class="text--white pb-5 pt-1">Well
              <span class="text-uppercase">
                {{ wellCoordinates(well(x, y).x_position, well(x, y).y_position) }}
               </span>
              is?
              </h2>
            <h4 class="text--white mb-3">
              Please choose your results if is Active (A) or Inactive (I)
            </h4>
            <b-btn variant="secondary" size="sm" class="mr-4"
              :class="{'selected': well(x, y).is_active === true}"
              @click="well(x, y).is_active = true">
              Active</b-btn>
            <b-btn variant="secondary" size="sm"
              :class="{'selected': well(x, y).is_active === false}"
              @click="well(x, y).is_active = false">
              Inactive</b-btn>
        </div>
      </div>
    </div>
  </div>
  <div class="info__box" v-if="activeWell">
    <img class="mr-3 info__ico" src="@/assets/ico-click.png" alt="">
    <div>
      <h2>{{ $t("results.well") }}
        <span class="text-uppercase">
          {{wellCoordinates(activeWell.x_position,activeWell.y_position)}}
        </span>
      </h2>
      <p class="text--small">
        {{ $t("results.info") }}
      </p>
    </div>
  </div>
  <div class="well__info" v-if="activeWell">
    <div class="d-flex justify-content-between">
      <div class="d-flex align-items-center">
        <h4 class="text--black mr-4">Initiator</h4>
          <div class="blocks__button initiator">{{ activeWell.initiator }}</div>
      </div>
      <div class="d-flex align-items-center">
        <h4 class="text--black mr-4">Monomers</h4>
        <div class="blocks__button monomer mr-3" v-if="activeWell.monomers.length"
          v-for="(monomer, index) in activeWell.monomers" :key="index">
          {{ monomer.name }}
        </div>
      </div>
      <div class="d-flex align-items-center">
        <h4 class="text--black mr-4">Terminator</h4>
        <div class="blocks__button terminator">{{ activeWell.terminator }}</div>
      </div>
    </div>
    <div class="d-flex justify-content-center pt-5">
      <div class="text-center well__all-details">
        <span class="well__details font-weight-bold pl-4">NAME</span>
      </div>
      <div class="text-center">
        <span class="well__details font-weight-bold">WEIGHT</span>
      </div>
    </div>
    <div v-for="(combination, index) in activeWell.combinations" :key="index"
      class="d-flex justify-content-center">
      <div class="text-center well__all-details">
        <span class="well__details">{{combination.name}}</span>
      </div>
      <div class="text-center">
        <span class="well__details">{{combination.weight}}</span>
      </div>
    </div>
  </div>
  <photos-list
    v-if="experiment"
    :photos.sync="photos"
    :newPhotos.sync="newPhotos"
    :deletedPhotos.sync="deletedPhotos"
    :isEditable="isEditable">
  </photos-list>
  <div v-if="isEditable">
      <div class="info__box mt-5 d-flex justify-content-center">
        <b-btn variant="primary" size="sm" class="mr-4 my-3" @click="saveResults" v-if="allResults">
          Save
        </b-btn>
        <b-btn variant="primary" size="sm" class="mr-4 my-3" @click="cancelResults">Cancel</b-btn>
        <b-btn variant="primary" size="sm" class="mr-4 my-3" @click="print">{{$t("results.print")}}
        </b-btn>
      </div>
  </div>
  <div v-else-if="experiment && isStudent">
    <div class="info__box mt-5 d-flex justify-content-center">
      <router-link :to="{ path: `/results/${experiment.id}` }"
        tag="button" class="btn btn-primary btn-sm mt-0 mr-3" v-scroll-to="'#scrollToOnLoad'">
        Edit
      </router-link>
      <b-btn
          variant="primary"
          size="sm"
          class="my-2 mr-3"
          @click="getSummary(experiment.id)">
          {{ $t("prevResults.generateResults") }}
        </b-btn>
        <b-btn variant="primary" size="sm" class="my-2" @click="print">
          {{$t("results.print")}}
        </b-btn>
    </div>
  </div>
</div>
</template>

<script>
import auth from '@/api/v1/auth';
import { mixin as clickaway } from 'vue-clickaway';
import game from '@/api/v1/game';
import PhotosList from '@/views/Game/Results/PhotosList';
import generateEmptyBoard from '@/utils/generateEmptyBoard';
import printMixin from '@/mixins/print';
import FileSaver from 'file-saver';

export default {
  components: {
    PhotosList,
  },
  props: ['isEditable'],
  data() {
    return {
      id: this.$route.params.experimentId,
      wells: [],
      experiment: null,
      activeWell: null,
      modalFor: null,
      photos: [],
      newPhotos: [],
      deletedPhotos: [],
      isStudent: auth.user.isStudent,
    };
  },
  computed: {
    allResults() {
      return !this.experiment.wells.some(well => well.is_active === null);
    },
  },
  methods: {
    getExperiment() {
      const succ = (response) => {
        this.experiment = response.data.experiment;
        this.wells = generateEmptyBoard(this.experiment.height, this.experiment.width);
        this.photos = response.data.experiment.photos;
      };
      game.getExperiment(this.id, succ);
    },
    wellCoordinates(x, y) {
      return `${String.fromCharCode(97 + y)}${x + 1}`;
    },
    wellClass(x, y) {
      const well = this.well(x, y);
      if (!well) return 'board__item--disabled';
      return {
        'board__item--selected': (this.activeWell && well.id === this.activeWell.id),
        'board__item--active': well.is_active === true,
        'board__item--inactive': well.is_active === false,
      };
    },
    selectBlock(well) {
      if (well) this.activeWell = well;
    },
    well(x, y) {
      return this.experiment.wells.find(well => (well.x_position === x) && (well.y_position === y));
    },
    showModal(well) {
      if (this.isEditable && well) this.modalFor = well;
    },
    closeModal() {
      this.modalFor = null;
    },
    saveResults() {
      const photos = [];
      this.newPhotos.forEach((photo) => {
        photos.push(photo.base64);
      });
      game.saveResults(
        this.experiment.id, photos, this.deletedPhotos, this.experiment.wells);
    },
    cancelResults() {
      this.$router.push({ name: 'prev_results' });
    },
    getSummary(id) {
      const succ = (response) => {
        const file = response.data;
        FileSaver.saveAs(file, `experiment-results-${id}.xlsx`);
      };
      game.getSummary(id, succ);
    },
    generateEmptyBoard,
  },
  mixins: [clickaway, printMixin],
  created() {
    this.getExperiment();
  },
};
</script>

<style lang="scss" scoped>
@import "./src/styles/helpers/_variables.scss";
@import "./src/styles/helpers/_include-media.scss";
@import "./src/styles/helpers/game_style.scss";

.board{
  &__item{
    cursor: pointer;
    box-shadow: 0;
    transition: box-shadow 0.3s ease;
    &--disabled{
      cursor: initial;
      background-color: #f3f3f3;
        .well__activity {
          display: none;
        }
    }
    &--active{
      background-color: #5dbb67;
      opacity: 0.5;
    }
    &--inactive{
      background-color: #ff6161;
      opacity: 0.5;
    }
    &--selected{
      opacity: 1;
      box-shadow: 5px 5px 0 0 rgba(0, 0, 0, 0.16);
    }
    &--ctrl{
      cursor: initial;
      background-color: #f9f9f9;
      .well__activity {
          display: none;
        }
    }
  }
}
.well{
  &__info {
    padding: 50px 200px;
  }
  &__activity {
    padding: 60px 90px;
    width: 550px;
    height: 350px;
    background-color: $color--orange;
    text-align: center;
    position: absolute;
    top: 145px;
    left: -225px;
    z-index: 2;
    user-select: none;
  }
  &__all-details {
    width: 300px;
  }
  &__details {
    font-size: 14px;
    color: $color--black;
    line-height: 18px;
  }
}
.info {
  &__box {
    background-color: $color--white-1;
    padding: 30px 100px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
  &__ico {
     width: 40px;
  }
}
.selected {
  background-color: $color--white;
  border-color: $color--orange;
  color: $color--orange;
}
</style>



// WEBPACK FOOTER //
// src/views/Game/Results/Board.vue